export const SUPPORTED_UI_LOCALES = ["ko", "en"] as const;

export type UiLocale = (typeof SUPPORTED_UI_LOCALES)[number];

export const DEFAULT_UI_LOCALE: UiLocale = "ko";
export const UI_LOCALE_STORAGE_KEY =
  "banryeojusik.ui-locale.v1";
export const UI_LOCALE_CHANGE_EVENT =
  "banryeojusik:ui-locale-change";
export const UI_LOCALE_CHANNEL_NAME =
  "banryeojusik.ui-locale";

interface StoredUiLocaleV1 {
  readonly version: 1;
  readonly locale: UiLocale;
  readonly updatedAt: number;
  readonly sourceId?: string;
}

export interface UiLocaleOption {
  readonly id: UiLocale;
  readonly languageTag: string;
  readonly shortLabel: string;
  readonly nativeLabel: string;
  readonly englishLabel: string;
}

export type UiLocaleMetadata = Omit<UiLocaleOption, "id">;

export const UI_LOCALE_METADATA = {
  ko: {
    languageTag: "ko-KR",
    shortLabel: "KO",
    nativeLabel: "한국어",
    englishLabel: "Korean",
  },
  en: {
    languageTag: "en-US",
    shortLabel: "EN",
    nativeLabel: "English",
    englishLabel: "English",
  },
} as const satisfies Readonly<Record<UiLocale, UiLocaleMetadata>>;

export const UI_LOCALE_OPTIONS: readonly UiLocaleOption[] = Object.freeze(
  SUPPORTED_UI_LOCALES.map((id) => ({
    id,
    ...UI_LOCALE_METADATA[id],
  })),
);

export function isUiLocale(value: unknown): value is UiLocale {
  return (
    typeof value === "string" &&
    (SUPPORTED_UI_LOCALES as readonly string[]).includes(value)
  );
}

export function languageTagForUiLocale(locale: UiLocale): string {
  return UI_LOCALE_METADATA[locale].languageTag;
}

/**
 * Chooses a first-run locale from the browser or operating-system language.
 * Korean devices start in Korean; every other language starts in English,
 * which is the international fallback until more catalogs are added.
 */
export function uiLocaleFromLanguageTags(
  languageTags: readonly string[] | undefined,
  fallback: UiLocale = DEFAULT_UI_LOCALE,
): UiLocale {
  const firstLanguageTag = languageTags?.find(
    (languageTag) => languageTag.trim() !== "",
  );
  if (!firstLanguageTag) {
    return fallback;
  }
  return /^ko(?:[-_]|$)/i.test(firstLanguageTag.trim()) ? "ko" : "en";
}

export function browserLanguageTags(
  navigatorTarget:
    | Pick<Navigator, "language" | "languages">
    | undefined,
): readonly string[] {
  if (!navigatorTarget) {
    return [];
  }
  const languages = Array.from(navigatorTarget.languages ?? []).filter(
    (language): language is string =>
      typeof language === "string" && language.trim() !== "",
  );
  if (languages.length > 0) {
    return languages;
  }
  return typeof navigatorTarget.language === "string"
    ? [navigatorTarget.language]
    : [];
}

export interface UiLocalePreference {
  readonly locale: UiLocale;
  readonly updatedAt: number;
  readonly sourceId: string;
}

export function compareUiLocalePreferences(
  left: UiLocalePreference,
  right: UiLocalePreference,
): number {
  if (left.updatedAt !== right.updatedAt) {
    return left.updatedAt - right.updatedAt;
  }
  if (left.sourceId !== right.sourceId) {
    return left.sourceId < right.sourceId ? -1 : 1;
  }
  if (left.locale === right.locale) {
    return 0;
  }
  return left.locale < right.locale ? -1 : 1;
}

export function parseStoredUiLocalePreference(
  value: string | null,
): UiLocalePreference | null {
  if (!value) {
    return null;
  }

  if (isUiLocale(value)) {
    return { locale: value, updatedAt: 0, sourceId: "legacy" };
  }

  try {
    const parsed = JSON.parse(value) as Partial<StoredUiLocaleV1>;
    return parsed.version === 1 && isUiLocale(parsed.locale)
      ? {
          locale: parsed.locale,
          updatedAt:
            typeof parsed.updatedAt === "number" &&
            Number.isFinite(parsed.updatedAt)
              ? parsed.updatedAt
              : 0,
          sourceId:
            typeof parsed.sourceId === "string" &&
            parsed.sourceId
              ? parsed.sourceId
              : "legacy",
        }
      : null;
  } catch {
    return null;
  }
}

function readStorage(
  storage: Storage | undefined,
): UiLocalePreference | null {
  if (!storage) {
    return null;
  }
  try {
    return parseStoredUiLocalePreference(
      storage.getItem(UI_LOCALE_STORAGE_KEY),
    );
  } catch {
    return null;
  }
}

function accessStorage(
  target:
    | Pick<Window, "localStorage" | "sessionStorage">
    | undefined,
  key: "localStorage" | "sessionStorage",
): Storage | undefined {
  if (!target) {
    return undefined;
  }
  try {
    return target[key];
  } catch {
    return undefined;
  }
}

export function canAccessUiLocaleStorage(
  target:
    | Pick<Window, "localStorage" | "sessionStorage">
    | undefined,
): boolean {
  if (!target) {
    return false;
  }
  return (["localStorage", "sessionStorage"] as const).some(
    (key) => {
      const storage = accessStorage(target, key);
      if (!storage) {
        return false;
      }
      try {
        storage.getItem(UI_LOCALE_STORAGE_KEY);
        return true;
      } catch {
        return false;
      }
    },
  );
}

export function readUiLocalePreference(
  target: Pick<Window, "localStorage" | "sessionStorage"> | undefined,
): UiLocalePreference | null {
  if (!target) {
    return null;
  }
  const candidates = [
    readStorage(accessStorage(target, "localStorage")),
    readStorage(accessStorage(target, "sessionStorage")),
  ].filter(
    (candidate): candidate is UiLocalePreference =>
      candidate !== null,
  );
  return (
    candidates.sort(
      (left, right) =>
        compareUiLocalePreferences(right, left),
    )[0] ?? null
  );
}

export function readUiLocale(
  target: Pick<Window, "localStorage" | "sessionStorage"> | undefined,
  languageTags?: readonly string[],
): UiLocale {
  return (
    readUiLocalePreference(target)?.locale ??
    uiLocaleFromLanguageTags(languageTags)
  );
}

export function writeUiLocalePreference(
  target:
    | Pick<Window, "localStorage" | "sessionStorage">
    | undefined,
  preference: UiLocalePreference,
): boolean {
  if (!target) {
    return false;
  }

  const serialized = JSON.stringify({
    version: 1,
    locale: preference.locale,
    updatedAt: preference.updatedAt,
    sourceId: preference.sourceId,
  } satisfies StoredUiLocaleV1);

  const localStorage = accessStorage(target, "localStorage");
  try {
    localStorage?.setItem(
      UI_LOCALE_STORAGE_KEY,
      serialized,
    );
    if (!localStorage) {
      throw new Error("local_storage_unavailable");
    }
    try {
      accessStorage(target, "sessionStorage")?.removeItem(
        UI_LOCALE_STORAGE_KEY,
      );
    } catch {
      // The durable write succeeded, so a stale session fallback is harmless.
    }
    return true;
  } catch {
    const sessionStorage = accessStorage(target, "sessionStorage");
    try {
      sessionStorage?.setItem(
        UI_LOCALE_STORAGE_KEY,
        serialized,
      );
      if (!sessionStorage) {
        return false;
      }
      return true;
    } catch {
      return false;
    }
  }
}

export function writeUiLocale(
  target:
    | Pick<Window, "localStorage" | "sessionStorage">
    | undefined,
  locale: UiLocale,
): boolean {
  return writeUiLocalePreference(target, {
    locale,
    updatedAt: Date.now(),
    sourceId: "direct-write",
  });
}
